/**
 * CustomVariablesRenderer - Start node custom variables editor
 *
 * Allows users to define custom key-value variables for use in workflow.
 * Note: This renderer does not support field mapping as it's for variable definition.
 */

import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { CustomVariable } from '@/types/imageWorkflow';
import type { BaseCustomRendererProps } from './types';

export const CustomVariablesRenderer: React.FC<BaseCustomRendererProps> = ({
  value,
  onChange,
  isDisabled = false,
}) => {
  const variables = (value as CustomVariable[]) || [];

  const handleAdd = () => {
    onChange([...variables, { key: '', value: '', description: '' }]);
  };

  const handleUpdate = (index: number, field: keyof CustomVariable, fieldValue: string) => {
    const updated = [...variables];
    updated[index] = { ...updated[index], [field]: fieldValue };
    onChange(updated);
  };

  const handleRemove = (index: number) => {
    onChange(variables.filter((_, i) => i !== index));
  };

  return (
    <div className="p-4 border border-cyan-200 rounded-lg bg-white space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">自定义变量</span>
          <span className="text-xs text-gray-500">定义可在后续节点使用的变量</span>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={isDisabled}
          className="flex items-center gap-1 px-2 py-1 text-xs text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-3 h-3" />
          添加变量
        </button>
      </div>

      {variables.length === 0 ? (
        <p className="text-sm text-gray-400 italic text-center py-4">
          暂无自定义变量，点击上方按钮添加
        </p>
      ) : (
        <div className="space-y-2">
          {variables.map((variable, index) => (
            <div key={index} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={variable.key}
                  onChange={(e) => handleUpdate(index, 'key', e.target.value)}
                  placeholder="变量名"
                  disabled={isDisabled}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-cyan-500 focus:border-cyan-500 disabled:bg-gray-100"
                />
                <input
                  type="text"
                  value={variable.value}
                  onChange={(e) => handleUpdate(index, 'value', e.target.value)}
                  placeholder="变量值"
                  disabled={isDisabled}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-cyan-500 focus:border-cyan-500 disabled:bg-gray-100"
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                disabled={isDisabled}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {variables.length > 0 && (
        <p className="text-xs text-gray-500">
          在后续节点中使用{' '}
          <code className="text-cyan-600 bg-cyan-50 px-1 rounded">{`{{var.变量名}}`}</code>{' '}
          引用自定义变量
        </p>
      )}
    </div>
  );
};

export default CustomVariablesRenderer;
